import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { T, FOCUS } from "../clientsTokens";

const CS = `${process.env.REACT_APP_BACKEND_URL}/api/customer-success`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted };

const Row = ({ k, v }) => (
  <div className="flex items-baseline gap-2 py-1" style={{ borderBottom: `1px solid ${T.dividerRow}` }}>
    <span style={{ ...LABEL, width: 110, flexShrink: 0 }}>{k}</span>
    <span className="text-[12.5px] break-all" style={{ color: T.ink }}>{v || "—"}</span>
  </div>
);

/* Confirm the HubSpot company before pulling anything: a wrong match would
   write another customer's history into this client's touch log. */
export const HubspotImportDialog = ({ client, onClose, onImported }) => {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${CS}/clients/${encodeURIComponent(client.client_id)}/hubspot/match`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Couldn't reach HubSpot" }));
  }, [client.client_id]);

  const co = state.match;

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${CS}/clients/${encodeURIComponent(client.client_id)}/hubspot/import`,
        { company_id: co.id });
      setResult(r.data);
      toast.success(r.data.imported ? `${r.data.imported} activities imported` : "Nothing new to import");
      await onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "The import failed"); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!window.confirm("Remove every activity that was imported from HubSpot for this client? Touches your team logged here are kept.")) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${CS}/clients/${encodeURIComponent(client.client_id)}/hubspot/imported`);
      toast.success(`${r.data.removed} imported activities removed`);
      setResult(null);
      await onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove them"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="hubspot-import-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[520px] flex flex-col"
        style={{ background: T.card, borderLeft: `1px solid ${T.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
          <div>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 17, fontWeight: 800, color: T.ink }}>
              Import activity from HubSpot
            </div>
            <div className="text-[12.5px]" style={{ color: T.ink2 }}>
              Calls, meetings, emails and notes logged against this company and its contacts, added to
              this client's activity. Safe to run again — nothing is duplicated.
            </div>
          </div>
          <button onClick={onClose} data-testid="hubspot-import-close"
            className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F6F4EF]"
            style={{ color: T.ink2 }}><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          {state.loading && (
            <div className="flex items-center gap-2 text-[13px]" style={{ color: T.ink2 }} data-testid="hubspot-import-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Finding this client in HubSpot…
            </div>
          )}

          {state.error && (
            <div className="rounded-[8px] p-2.5 text-[12.5px]" data-testid="hubspot-import-error"
              style={{ background: "#FFF8D6", border: `1px solid #E8C86A`, color: "#6B5600" }}>{state.error}</div>
          )}

          {!state.loading && !state.error && !co && (
            <div className="text-[13px]" style={{ color: T.ink }} data-testid="hubspot-import-nomatch">
              No HubSpot company matches <strong>{client.company_name}</strong>. The names have to line up
              (or the domain has to match) before history can be pulled across.
            </div>
          )}

          {co && (
            <>
              <div>
                <div style={LABEL}>The company we matched</div>
                <div className="mt-1.5 rounded-[8px] p-3" style={{ background: T.panel, border: `1px solid ${T.dividerInner}` }}
                  data-testid="hubspot-import-match">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[14px] font-bold" style={{ color: T.ink }}>{co.name}</div>
                    <a href={co.hubspot_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: T.accentInk }}
                      data-testid="hubspot-import-open">
                      Check in HubSpot <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="mt-1.5">
                    <Row k="Domain" v={co.domain} />
                    <Row k="Location" v={[co.city, co.country].filter(Boolean).join(", ")} />
                    <Row k="Lifecycle" v={co.lifecycle_stage} />
                    <Row k="Owner" v={(co.owner || {}).full_name} />
                    <Row k="Contacts" v={`${co.contact_count} linked contact${co.contact_count === 1 ? "" : "s"} — their activity comes across too`} />
                  </div>
                </div>
                {state.last_imported_at && (
                  <div className="text-[11.5px] mt-1.5" style={{ color: T.ink2 }} data-testid="hubspot-import-last">
                    Last imported {String(state.last_imported_at).slice(0, 10)}. Running it again only adds what's new.
                  </div>
                )}
              </div>

              {result && (
                <div className="rounded-[8px] p-3 space-y-1" data-testid="hubspot-import-result"
                  style={{ background: T.inset, border: `1px solid ${T.dividerInner}` }}>
                  <div className="text-[13px] font-bold" style={{ color: T.ink }}>
                    {result.imported} imported{result.already_there ? `, ${result.already_there} already here` : ""}
                  </div>
                  <div className="text-[12.5px]" style={{ color: T.ink2 }}>
                    {Object.entries(result.by_type || {}).map(([k, n]) => `${n} ${k}${n === 1 ? "" : "s"}`).join(" · ") || "Nothing new"}
                  </div>
                  {!!(result.contact_names || []).length && (
                    <div className="text-[11.5px]" style={{ color: T.ink2 }}>
                      Followed {result.contact_names.join(", ")}
                    </div>
                  )}
                  {!!(result.skipped_types || []).length && (
                    <div className="text-[11.5px]" style={{ color: T.ink2 }}>
                      HubSpot tasks were left out — they're someone's to-do, not contact with the customer,
                      so counting them would flatter every last-touch date.
                    </div>
                  )}
                  {(result.warnings || []).map((w, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[12px] mt-1" style={{ color: "#6B5600" }}
                      data-testid={`hubspot-import-warning-${i}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{w} Add <strong>sales-email-read</strong> to the HubSpot private app, then run this again to bring the emails across.</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
          {state.last_imported_at && (
            <button onClick={undo} disabled={busy} data-testid="hubspot-import-undo"
              className={`h-9 px-3 text-[12.5px] font-semibold ${FOCUS}`} style={{ color: T.destructive }}>
              Remove imported
            </button>
          )}
          <button onClick={onClose} className="h-9 px-3 text-[13px] ml-auto" style={{ color: T.ink2 }}>
            {result ? "Done" : "Cancel"}
          </button>
          {co && (
            <button onClick={run} disabled={busy} data-testid="hubspot-import-run"
              className="h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60"
              style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
              {busy ? "Importing…" : result ? "Run again" : "Import history"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
